import React, { useCallback, useMemo, useEffect, useState } from 'react';
import eventBus from '../EventBus';

type LoadingProps = {};

const LoadingScreen: React.FC<LoadingProps> = () => {
    const [progress, setProgress] = useState(0);
    const [toLoad, setToLoad] = useState(0);
    const [loaded, setLoaded] = useState(0);
    const [loadingOverlayOpacity, setLoadingOverlayOpacity] = useState(1);
    const [loadingTextOpacity, setLoadingTextOpacity] = useState(1);
    const [startPopupOpacity, setStartPopupOpacity] = useState(0);
    const [webGLErrorOpacity, setWebGLErrorOpacity] = useState(0);

    const [showBiosInfo, setShowBiosInfo] = useState(false);
    const [showLoadingResources, setShowLoadingResources] = useState(false);
    const [doneLoading, setDoneLoading] = useState(false);
    const [webGLError, setWebGLError] = useState(false);
    const [counter, setCounter] = useState(0);
    const [resources, setResources] = useState<string[]>([]);
    const [mobileWarning, setMobileWarning] = useState(window.innerWidth < 768);
    const [art, setArt] = useState('');

    const onResize = () => {
        setMobileWarning(window.innerWidth < 768);
    };

    useEffect(() => {
        window.addEventListener('resize', onResize);

        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('debug')) {
            start();
        }
        if (!detectWebGLContext()) {
            setWebGLError(true);
        } else {
            setShowBiosInfo(true);
        }

        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        eventBus.on('loadedSource', (data) => {
            setProgress(data.progress);
            setToLoad(data.toLoad);
            setLoaded(data.loaded);
            const resourceMessage = `Executing chmod +x ${
                data.sourceName
            } . ${Math.round(data.progress * 100)}%`;
            setResources((prevResources) => {
                const updatedResources = [...prevResources, resourceMessage];
                if (updatedResources.length > 11) {
                    updatedResources.shift();
                }
                return updatedResources;
            });
        });
    }, []);

    useEffect(() => {
        setShowLoadingResources(true);
        setCounter(counter + 1);
    }, [loaded]);

    useEffect(() => {
        if (progress >= 1 && !webGLError) {
            setDoneLoading(true);
            setTimeout(() => {
                setLoadingTextOpacity(0);
                setTimeout(() => {
                    setStartPopupOpacity(1);
                }, 500);
            }, 1000);
        }
    }, [progress]);

    useEffect(() => {
        if (webGLError) {
            setTimeout(() => {
                setWebGLErrorOpacity(1);
            }, 500);
        }
    }, [webGLError]);

    const start = useCallback(() => {
        setLoadingOverlayOpacity(0);
        eventBus.dispatch('loadingScreenDone', {});
        const ui = document.getElementById('ui');
        if (ui) {
            ui.style.pointerEvents = 'none';
        }
    }, []);

    const detectWebGLContext = () => {
        let canvas = document.createElement('canvas');
        let gl =
            canvas.getContext('webgl') ||
            canvas.getContext('experimental-webgl');
        return gl && gl instanceof WebGLRenderingContext;
    };

    const getCurrentDate = () => {
        const date = new Date();
        const time = date.toTimeString().split(' ')[0];
        return `Last login: ${date.toDateString()} ${time} on ttys000`;
    };

    const progressBarWidth = `${Math.round(progress * 100)}%`;
    const maxResourceLength = 60; // Set max length for resource strings
    const paddedResources = resources.map((resource) => {
        const name = resource.split('.')[0];
        const percentage = resource.split('.')[1];
        const padded =
            name + '.'.repeat(maxResourceLength - resource.length) + percentage;
        return padded;
    });

    return (
        <div
            style={{
                ...styles.overlay,
                opacity: loadingOverlayOpacity,
                transform: `scale(${loadingOverlayOpacity === 0 ? 1.1 : 1})`,
            }}
        >
            <div className="ascii-art"></div>
            {!webGLError && (
                <div
                    style={{
                        ...styles.overlayText,
                        opacity: loadingTextOpacity,
                    }}
                >
                    <div
                        style={styles.header}
                        className="loading-screen-header"
                    >
                        <p>Welcome to Yash Bhappa's Portfolio</p>
                        <br />
                        <p>Initializing modules...</p>
                    </div>
                    <div style={styles.body}>
                        {showBiosInfo && (
                            <>
                                {progress < 1 ? (
                                    <p className="loading">
                                        LOADING RESOURCES ({loaded}/
                                        {toLoad || '-'})
                                        {` [${'█'.repeat(
                                            Math.floor(progress * 10)
                                        )}${'-'.repeat(
                                            10 - Math.floor(progress * 10)
                                        )}] ${Math.round(
                                            progress * 100
                                        )}% complete`}
                                    </p>
                                ) : (
                                    <p>FINISHED LOADING RESOURCES</p>
                                )}
                            </>
                        )}
                        <div style={styles.resourcesLoadingList}>
                            {paddedResources.map((sourceName) => (
                                <p key={sourceName}>{sourceName}</p>
                            ))}
                        </div>
                    </div>
                    <div style={styles.spacer} />
                    <div style={styles.body}>
                        {showLoadingResources && doneLoading && (
                            <p>
                                All Content Loaded, launching{' '}
                                <b style={styles.green}>
                                    'Yash Bhappa Portfolio'
                                </b>{' '}
                                V1.0
                            </p>
                        )}
                    </div>
                    <div
                        style={styles.footer}
                        className="loading-screen-footer"
                    >
                        <p>
                            Press <b>DEL</b> to enter SETUP, <b>ESC</b> to skip
                            memory test
                        </p>
                        <br />
                        <p>{getCurrentDate()}</p>
                    </div>
                </div>
            )}

            {webGLError && (
                <div
                    style={{
                        ...styles.popupContainer,
                        opacity: webGLErrorOpacity,
                    }}
                >
                    <div style={styles.startPopup}>
                        <p>
                            <b style={{ color: 'red' }}>ERROR:</b> WebGL context
                            not found.
                        </p>
                        <p>Please enable WebGL or switch browsers.</p>
                    </div>
                </div>
            )}

            <div
                style={{ ...styles.popupContainer, opacity: startPopupOpacity }}
            >
                <div style={styles.startPopup}>
                    <p>Yash Bhappa's Portfolio</p>
                    <br />
                    {mobileWarning && (
                        <p style={styles.warning}>
                            WARNING: Mobile view may not be fully supported. Use
                            a larger display for the best experience.
                        </p>
                    )}
                    <p>
                        Click <b>start</b> to begin{' '}
                        <span className="blinking-cursor">_</span>
                    </p>
                    <br />
                    <div className="bios-start-button" onClick={start}>
                        <p>START</p>
                    </div>
                </div>
            </div>

            <div style={styles.progressBarContainer}>
                <div
                    style={{ ...styles.progressBar, width: progressBarWidth }}
                />
            </div>
        </div>
    );
};

const styles: StyleSheetCSS = {
    overlay: {
        backgroundColor: 'black',
        width: '100%',
        height: '100%',
        display: 'flex',
        transition: 'opacity 0.2s, transform 0.2s ease-in-out',
        fontSize: 16,
        color: '#00ff00',
        letterSpacing: 0.8,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    header: {
        width: '100%',
        textAlign: 'center',
        marginBottom: 16,
    },
    body: {
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
    },
    resourcesLoadingList: {
        marginTop: 16,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'monospace',
    },
    footer: {
        textAlign: 'center',
        marginTop: 16,
        fontFamily: 'monospace',
    },
    startPopup: {
        backgroundColor: '#000',
        padding: 24,
        border: '7px solid #fff',
        textAlign: 'center',
        maxWidth: 500,
    },
    warning: {
        color: 'yellow',
    },
    popupContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    progressBarContainer: {
        width: '80%',
        height: 20,
        backgroundColor: '#444',
        marginTop: 20,
        position: 'absolute',
        bottom: 0,
    },
    progressBar: {
        height: '100%',
        backgroundColor: '#ffffff',
        transition: 'width 0.3s ease-in-out',
    },
    asciiArt: {
        fontFamily: 'monospace',
        color: '#00ff00',
        marginTop: 20,
        whiteSpace: 'pre-wrap',
        textAlign: 'center',
    },
    overlayText: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
    logoContainer: {
        display: 'flex',
        flexDirection: 'row',
    },
    green: {
        color: '#00ff00',
    },
    logoImage: {
        width: 64,
        height: 42,
        imageRendering: 'pixelated',
        marginRight: 16,
    },
    progressFill: {
        height: '100%',
        backgroundColor: '#76c7c0',
        transition: 'width 0.4s ease-in-out',
    },
    tipText: {
        color: '#fff',
        textAlign: 'center',
    },
    spacer: {
        height: 16,
    },
    headerInfo: {
        marginLeft: 64,
    },
    link: {
        color: '#4598ff',
        cursor: 'pointer',
    },
};

export default LoadingScreen;
